import {
	DateTimeField,
	FormattedDateTime,
	IDataListColumn,
	IconButton,
	PaginatedAutoDataList,
	laravelPaginatedListFetchConfig,
	useHasPermission,
} from "@dgs/core";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { PermissionName } from "../../../shared/types/permission";
import { AppNotificationStatusSelect } from "../appNotifications/AppNotificationStatusSelect";
import { appNotificationService } from "../shared/api/appNotificationService";
import { AppNotificationIndexResource, AppNotificationStatus } from "../shared/types/appNotification";

export const AppNotificationList: FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const columns = useMemo<IDataListColumn<AppNotificationIndexResource>[]>(
		() => [
			{
				type: "value",
				heading: t("Title"),
				valueKey: "title",
			},
			{
				type: "value",
				heading: t("Created by"),
				valueKey: "author",
				render: ({ author }) => author?.name,
			},
			{
				type: "value",
				heading: t("Status"),
				valueKey: "status",
				render: ({ status }) => t(status),
				filter: {
					operators: ["="],
					renderActiveColumnFilterValue: (status: AppNotificationStatus) => t(status),
					renderValueField: ({ fieldName }) => <AppNotificationStatusSelect label={t("Status")} name={fieldName} />,
				},
				sortable: true,
			},
			{
				type: "value",
				heading: t("Scheduled for"),
				valueKey: "sendAt",
				render: ({ sendAt }) => (sendAt ? <FormattedDateTime dateTime={sendAt} /> : "-"),
				filter: {
					operators: ["IS_EMPTY", ">", "<"],
					renderActiveColumnFilterValue: (value: Date | null) =>
						value ? <FormattedDateTime dateTime={value} /> : null,
					renderValueField: ({ fieldName, operator }) =>
						operator === "IS_EMPTY" ? null : <DateTimeField label={t("Scheduled for")} name={fieldName} />,
				},
				sortable: true,
			},
			{
				type: "value",
				heading: t("Sent at"),
				valueKey: "sentAt",
				render: ({ sentAt }) => (sentAt ? <FormattedDateTime dateTime={sentAt} /> : "-"),
				filter: {
					operators: ["IS_EMPTY", ">", "<"],
					renderActiveColumnFilterValue: (value: Date | null) =>
						value ? <FormattedDateTime dateTime={value} /> : null,
					renderValueField: ({ fieldName, operator }) =>
						operator === "IS_EMPTY" ? null : <DateTimeField label={t("Sent at")} name={fieldName} />,
				},
				sortable: true,
			},
		],
		[t],
	);

	const canCreate = useHasPermission<PermissionName>(["appNotification.create"]);
	const canUpdate = useHasPermission<PermissionName>(["appNotification.update"]);

	return (
		<PaginatedAutoDataList
			name="notifications"
			heading={t("Notifications")}
			empty={t("No notifications available.")}
			columns={columns}
			listFetchConfig={laravelPaginatedListFetchConfig(appNotificationService.listKey, appNotificationService.index)}
			headerActions={
				canCreate && (
					<IconButton icon="plus" title={t("Add")} color="primary" size="small" onClick={() => navigate("new")} />
				)
			}
			onRowClick={canUpdate ? ({ id }) => navigate(`${id}`) : undefined}
			initialSearch={{ search: "", searchFields: [{ name: "title", label: t("Title") }] }}
		/>
	);
};
